import React, { useState, useEffect } from "react";
import Modal from 'react-modal';
import Select from "react-select";
import { useSelector } from "react-redux";
import { RootState } from "../../slices/store";
import useIsMobile from "src/hooks/useIsMobile";

interface AdvanceFilterModalProps {
  modalIsOpen: boolean;
  closeModal: () => void;
  handleRateTypeChange: (selectedOptions: any) => void;
  handleExchangeTypeChange: (selectedOptions: any) => void;
  handleMediaTypeChange: (selectedOptions: any) => void;
  handleAdFormatChange: (selectedOptions: any) => void;
  applyFiltersToRedux: () => void;
  rateTypes: any[];
  exchangeTypes: any[];
  mediaTypes: any[];
  adFormats: any[];
  rateTypeOptions: any[];
  exchangeTypeOptions: any[];
  mediaTypeOptions: any[];
  adFormatOptions: any[];
  customTagStyles: any;
}

const AdvanceFilterModal: React.FC<AdvanceFilterModalProps> = ({
  modalIsOpen,
  closeModal,
  handleRateTypeChange,
  handleExchangeTypeChange,
  handleMediaTypeChange,
  handleAdFormatChange,
  applyFiltersToRedux,
  rateTypes,
  exchangeTypes,
  mediaTypes,
  adFormats,
  rateTypeOptions,
  exchangeTypeOptions,
  mediaTypeOptions,
  adFormatOptions,
  customTagStyles,
}) => {
  const filters = useSelector((state: RootState) => state.filters);
  const { user } = useSelector((state: any) => state.login);

  const isRestrictedUser = React.useMemo(() => {
    if (!user?.user) return true;

    const userType = user.user.user_type;
    const restrictedTypes = ["Accounting", "Read Only Admin", "Sales Support Coordinator", "Integrated-User"];

    return restrictedTypes.includes(userType) || userType === 'Client';
  }, [user]);

  const handleClose = () => {
    const shouldReset = !filters.selectedRateType && !filters.selectedExchangeType && !filters.selectedMediaType && !filters.selectedAdFormat;

    handleRateTypeChange(shouldReset ? [] : initialRateTypes);
    handleExchangeTypeChange(shouldReset ? [] : initialExchangeTypes);
    handleMediaTypeChange(shouldReset ? [] : initialMediaTypes);
    handleAdFormatChange(shouldReset ? [] : initialAdFormats);
    closeModal();
  }

  const handleClearAll = () => {
    handleRateTypeChange([]);
    handleExchangeTypeChange([]);
    handleMediaTypeChange([]);
    handleAdFormatChange([]);
  }

  const isMobile = useIsMobile();
  const [initialRateTypes, setInitialRateTypes] = useState<any[]>([]);
  const [initialExchangeTypes, setInitialExchangeTypes] = useState<any[]>([]);
  const [initialMediaTypes, setInitialMediaTypes] = useState<any[]>([]);
  const [initialAdFormats, setInitialAdFormats] = useState<any[]>([]);

  useEffect(() => {
    if (modalIsOpen) {
      setInitialRateTypes([...rateTypes]);
      setInitialExchangeTypes([...exchangeTypes]);
      setInitialMediaTypes([...mediaTypes]);
      setInitialAdFormats([...adFormats]);
    }
  }, [modalIsOpen]);

  const hasSelections = rateTypes.length > 0 || exchangeTypes.length > 0 || mediaTypes.length > 0 || adFormats.length > 0;

  const selectStyles = {
    ...customTagStyles,
    menuList: (base: any) => ({
      ...base,
      maxHeight: '200px',
      overflowY: 'auto',
    }),
    input: (provided: any) => ({
      ...provided,
      fontSize: '16px',
    }),
    option: (base: any) => ({
      ...base,
      backgroundColor: 'transparent',
      color: 'inherit',
      ':active': {
        backgroundColor: 'transparent',
      },
    }),
  };

  return (
    <Modal
      isOpen={modalIsOpen}
      onRequestClose={handleClose}
      shouldCloseOnOverlayClick={true}
      className="advance-filter-modal"
      contentLabel="Advance Filter Modal"
    >
      <div className="d-flex justify-content-between align-items-center py-2">
        <h5>Advanced Filters</h5>
        <button onClick={handleClose} className="btn-close" aria-label="Close Modal">
        </button>
      </div>
      <div className="advance-filter-body">
        <div className="advance-filter-description">
          <span>Advanced filters allow you to fetch specific data based on your criteria.</span>
        </div>
        <div className="d-flex flex-column gap-3 py-3">
          {!isRestrictedUser && (
            <div className="dropdown-container-rate">
              <label htmlFor="rate-type-filter">Rate Type: </label>
              <Select
                className="dropdown rate-type-select"
                classNamePrefix="react-select"
                inputId="rate-type-filter"
                isMulti
                options={rateTypeOptions}
                onChange={handleRateTypeChange}
                value={rateTypes}
                placeholder="Select Rate Type"
                menuPlacement="auto"
                styles={selectStyles}
              />
            </div>
          )}

          <div className="dropdown-container-exchange">
            <label htmlFor="exchange-type-filter">Exchange Type: </label>
            <Select
              className="dropdown exchange-type-select"
              classNamePrefix="react-select"
              inputId="exchange-type-filter"
              isMulti
              options={exchangeTypeOptions}
              onChange={handleExchangeTypeChange}
              value={exchangeTypes}
              placeholder="Select Exchange Type"
              menuPlacement={isMobile ? "top" : "bottom"}
              styles={selectStyles}
            />
          </div>

          <div className="dropdown-container-media">
            <label htmlFor="media-type-filter">Media Type: </label>
            <Select
              className="dropdown media-type-select"
              classNamePrefix="react-select"
              inputId="media-type-filter"
              isMulti
              options={mediaTypeOptions}
              onChange={handleMediaTypeChange}
              value={mediaTypes}
              placeholder="Select Media Type"
              menuPlacement={isMobile ? "top" : "bottom"}
              styles={selectStyles}
            />
          </div>

          <div className="dropdown-container-ad-format">
            <label htmlFor="ad-format-filter">Ad Format: </label>
            <Select
              className="dropdown ad-format-select"
              classNamePrefix="react-select"
              inputId="ad-format-filter"
              isMulti
              options={adFormatOptions}
              onChange={handleAdFormatChange}
              value={adFormats}
              placeholder="Select Ad Format"
              menuPlacement={isMobile ? "top" : "bottom"}
              styles={selectStyles}
            />
          </div>
        </div>
        <div className="advance-filter-actions">
          {hasSelections && (
            <button
              type="button"
              className="btn btn-danger button-secondary advance-filter-btn"
              onClick={handleClearAll}
            >
              Clear All
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary button-primary advance-filter-btn"
            onClick={(e) => {
              e.preventDefault();
              applyFiltersToRedux();
              closeModal();
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AdvanceFilterModal;
